/**
 * QuarterlyMeetingDialog - Summary Report of Meeting Form
 * Based on Excel Health Services template
 * Extracted from ClientDetailPage
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Save, Loader2, Plus, Trash2, Users, FileText, Printer } from "lucide-react";

export function QuarterlyMeetingDialog({
  open,
  onOpenChange,
  clientName,
  newQuarterlyMeeting,
  setNewQuarterlyMeeting,
  saving,
  onSave,
  editingMeetingId,
}) {
  // Local state for attendance list
  const [attendees, setAttendees] = useState(
    newQuarterlyMeeting.attendees_list || [{ name: "", title: "" }]
  );

  // Add new attendee row
  const addAttendee = () => {
    setAttendees([...attendees, { name: "", title: "" }]);
  };

  // Remove attendee row
  const removeAttendee = (index) => {
    if (attendees.length > 1) {
      const updated = attendees.filter((_, i) => i !== index);
      setAttendees(updated);
      setNewQuarterlyMeeting((prev) => ({
        ...prev,
        attendees_list: updated,
        attendees: updated.map(a => a.name).filter(n => n).join(", "),
      }));
    }
  };

  // Update attendee
  const updateAttendee = (index, field, value) => {
    const updated = [...attendees];
    updated[index][field] = value;
    setAttendees(updated);
    setNewQuarterlyMeeting((prev) => ({
      ...prev,
      attendees_list: updated,
      attendees: updated.map(a => a.name).filter(n => n).join(", "),
    }));
  };

  // Print function
  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Summary Report of Meeting</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; }
            .company-name { font-size: 24px; font-weight: bold; color: #0d9488; margin-bottom: 5px; }
            .report-title { font-size: 18px; font-weight: bold; text-decoration: underline; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; background: #f1f5f9; padding: 8px; border-left: 4px solid #0d9488; }
            .field-row { display: flex; margin-bottom: 10px; }
            .field-label { font-weight: bold; width: 180px; }
            .field-value { flex: 1; border-bottom: 1px solid #ccc; padding-bottom: 2px; min-height: 20px; }
            .notes-section { margin-top: 20px; }
            .notes-field { margin-bottom: 15px; }
            .notes-label { font-weight: bold; margin-bottom: 5px; }
            .notes-value { border: 1px solid #ddd; padding: 10px; min-height: 60px; background: #fafafa; }
            .attendance-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .attendance-table th, .attendance-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            .attendance-table th { background: #f1f5f9; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; text-align: center; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">Excel Health Services</div>
            <div class="report-title">SUMMARY REPORT OF MEETING</div>
          </div>
          
          <div class="section">
            <div class="field-row">
              <span class="field-label">Type of Meeting:</span>
              <span class="field-value">${newQuarterlyMeeting.meeting_type || "Quarterly Meeting"}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Presenter:</span>
              <span class="field-value">${newQuarterlyMeeting.presenter || ""}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Date:</span>
              <span class="field-value">${newQuarterlyMeeting.meeting_date || ""}</span>
            </div>
          </div>

          <div class="notes-section">
            <div class="section-title">MEETING NOTES</div>
            
            <div class="notes-field">
              <div class="notes-label">METHOD OF PRESENTATION:</div>
              <div class="notes-value">${newQuarterlyMeeting.method_of_presentation || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">SUBJECTS COVERED:</div>
              <div class="notes-value">${newQuarterlyMeeting.subjects_covered || newQuarterlyMeeting.topics_discussed || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">REACTION FROM PARTICIPANTS:</div>
              <div class="notes-value">${newQuarterlyMeeting.reaction_from_participants || ""}</div>
            </div>
            
            <div class="notes-field">
              <div class="notes-label">SUMMARY AND CONCLUSIONS:</div>
              <div class="notes-value">${newQuarterlyMeeting.summary_and_conclusions || newQuarterlyMeeting.action_items || ""}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">IN ATTENDANCE (Please Sign)</div>
            <table class="attendance-table">
              <thead>
                <tr>
                  <th style="width: 50%">Name</th>
                  <th style="width: 50%">Title</th>
                </tr>
              </thead>
              <tbody>
                ${attendees.map(a => `
                  <tr>
                    <td>${a.name || ""}</td>
                    <td>${a.title || ""}</td>
                  </tr>
                `).join("")}
                ${attendees.length < 8 ? Array(8 - attendees.length).fill("").map(() => `
                  <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                  </tr>
                `).join("") : ""}
              </tbody>
            </table>
          </div>

          <div class="footer">
            Client: ${clientName} | Generated: ${new Date().toLocaleDateString()}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                Summary Report of Meeting
              </DialogTitle>
              <DialogDescription>
                {editingMeetingId ? "Edit" : "Record"} quarterly meeting for {clientName}
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-2"
              data-testid="print-meeting-btn"
            >
              <Printer className="w-4 h-4" />
              Print
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Meeting Details Section */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              Meeting Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Type of Meeting</Label>
                <Input
                  value={newQuarterlyMeeting.meeting_type || "Quarterly Meeting"}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      meeting_type: e.target.value,
                    }))
                  }
                  placeholder="Quarterly Meeting"
                  data-testid="meeting-type-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Presenter *</Label>
                <Input
                  value={newQuarterlyMeeting.presenter || ""}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      presenter: e.target.value,
                    }))
                  }
                  placeholder="Presenter name"
                  data-testid="presenter-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newQuarterlyMeeting.meeting_date}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      meeting_date: e.target.value,
                    }))
                  }
                  data-testid="meeting-date-input"
                />
              </div>
            </div>
          </div>

          {/* Meeting Notes Section */}
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h3 className="font-semibold text-slate-800 mb-4 border-b pb-2">
              MEETING NOTES
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-medium">METHOD OF PRESENTATION</Label>
                <Input
                  value={newQuarterlyMeeting.method_of_presentation || ""}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      method_of_presentation: e.target.value,
                    }))
                  }
                  placeholder="Lecture, Demonstration, Films, etc."
                  data-testid="method-presentation-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">SUBJECTS COVERED</Label>
                <Textarea
                  value={newQuarterlyMeeting.subjects_covered || newQuarterlyMeeting.topics_discussed || ""}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      subjects_covered: e.target.value,
                      topics_discussed: e.target.value,
                    }))
                  }
                  placeholder="Topics and subjects covered during the meeting..."
                  rows={3}
                  data-testid="subjects-covered-textarea"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">REACTION FROM PARTICIPANTS</Label>
                <Textarea
                  value={newQuarterlyMeeting.reaction_from_participants || ""}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      reaction_from_participants: e.target.value,
                    }))
                  }
                  placeholder="How participants responded to the presentation..."
                  rows={3}
                  data-testid="reaction-textarea"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-medium">SUMMARY AND CONCLUSIONS</Label>
                <Textarea
                  value={newQuarterlyMeeting.summary_and_conclusions || newQuarterlyMeeting.action_items || ""}
                  onChange={(e) =>
                    setNewQuarterlyMeeting((prev) => ({
                      ...prev,
                      summary_and_conclusions: e.target.value,
                      action_items: e.target.value,
                    }))
                  }
                  placeholder="Key conclusions and action items from the meeting..."
                  rows={4}
                  data-testid="summary-conclusions-textarea"
                />
              </div>
            </div>
          </div>

          {/* Attendance Section */}
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-600" />
                IN ATTENDANCE (Please Sign)
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAttendee}
                className="gap-1"
                data-testid="add-attendee-btn"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2 text-sm font-semibold text-slate-700 w-[45%]">
                      Name
                    </th>
                    <th className="text-left px-4 py-2 text-sm font-semibold text-slate-700 w-[45%]">
                      Title
                    </th>
                    <th className="w-[10%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.map((attendee, index) => (
                    <tr key={index} className="border-t border-slate-200">
                      <td className="px-2 py-2">
                        <Input
                          value={attendee.name}
                          onChange={(e) => updateAttendee(index, "name", e.target.value)}
                          placeholder="Attendee name"
                          className="border-0 bg-transparent focus:ring-0"
                          data-testid={`attendee-name-${index}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={attendee.title}
                          onChange={(e) => updateAttendee(index, "title", e.target.value)}
                          placeholder="Title/Role"
                          className="border-0 bg-transparent focus:ring-0"
                          data-testid={`attendee-title-${index}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {attendees.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttendee(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            data-testid={`remove-attendee-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Add all meeting attendees with their names and titles
            </p>
          </div>

          {/* Next Meeting Date (optional) */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="space-y-2">
              <Label>Next Meeting Date (Optional)</Label>
              <Input
                type="date"
                value={newQuarterlyMeeting.next_meeting_date || ""}
                onChange={(e) =>
                  setNewQuarterlyMeeting((prev) => ({
                    ...prev,
                    next_meeting_date: e.target.value,
                  }))
                }
                className="w-48"
                data-testid="next-meeting-date-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !newQuarterlyMeeting.meeting_date}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-quarterly-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Meeting
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
